import { LogicalPath, SitemapEntry, TenantPath, UserPath } from '@msdk/definitions'
import { matchPath, useLocation } from 'react-router-dom'

import { useSitemapItems } from '../../state/sitemap'
import { createFunction, processCondition } from '../../utils'

export interface RouteParamsResult {
  entityName?: string
  moduleName?: string
  itemId?: string
}
const evaluationResults = new Map<string, boolean>()

function evaluateVisibility(entry: SitemapEntry): boolean {
  if (!entry.isVisible) {
    return true
  }

  const cached = evaluationResults.get(entry.isVisible)

  if (cached) {
    return cached
  }

  const func = createFunction(processCondition(entry.isVisible))
  const visible = !!func({
    entity: undefined,
    row: undefined,
    logic: new LogicalPath(),
    user: new UserPath(),
    tenant: new TenantPath(),
  })

  evaluationResults.set(entry.isVisible, visible)
  return visible
}

function toPair(entityName: string | undefined, entry: SitemapEntry): string {
  return entry.entity !== entityName ? `${entry.entity}:${entry.viewName}` : `${entry.viewName}`
}

/**
 * This hook abstracts route parameters from various routes.
 * It handles old view routes (with comma-separated view names),
 * custom routes pointing to custom components,
 * and new view routes containing only the item ID in the URL.
 * It transparently returns the route parameters,
 * eliminating the need for the hook user to know the specific originating route.
 */
export function useRouteParams(): RouteParamsResult {
  const { pathname } = useLocation()

  const viewParams = matchPath(':tenant/:sitemap/:entityName/:moduleName', pathname)
  const sitemapParams = matchPath(':tenant/:sitemap/:sitemapId', pathname)
  const customParams = matchPath(':tenant/:sitemap/:entityName/:moduleName/*', pathname)

  const items = useSitemapItems()

  if (viewParams) {
    return { entityName: viewParams.params.entityName, moduleName: viewParams.params.moduleName }
  }

  if (customParams) {
    return { entityName: customParams.params.entityName, moduleName: customParams.params.moduleName }
  }

  if (sitemapParams) {
    const sitemapItem = items.find((item) => item.id === sitemapParams.params.sitemapId)
    const entityName = sitemapItem?.entries[0].entity
    const pairs = sitemapItem?.entries.filter(evaluateVisibility).map((e) => toPair(entityName, e)) || []
    return { entityName, moduleName: pairs.join(','), itemId: sitemapItem?.id }
  }

  return { entityName: undefined, moduleName: undefined }
}
